import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SEOHead from '../../components/SEO/SEOHead';
import SEOFooter from '../../components/SEO/SEOFooter';
import { Brain, BookOpen, ChevronRight, GraduationCap, Search, BarChart3, Calculator, Trophy, Target, TrendingUp, AlertCircle, CheckCircle, Users, Lightbulb, Shield, Award, Database, Cpu, ChevronDown, Play, PauseCircle, Info, Filter } from 'lucide-react';

const TutorialAIFutebolPage = () => {
  const navigate = useNavigate();
  const [expandedSections, setExpandedSections] = useState({});
  const [activeLesson, setActiveLesson] = useState(0);
  const [filterLevel, setFilterLevel] = useState('all');

  const handleStartChat = () => {
    navigate('/chat');
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const lessons = [
    {
      title: "Introdução: IA no Futebol",
      icon: Brain,
      duration: "5 min",
      topics: [
        "O que é Inteligência Artificial",
        "Como funciona no futebol",
        "Diferença entre análise e apostas",
        "Por que pesquisa é fundamental"
      ],
      keywords: "inteligência artificial futebol, IA esportes, análise futebol, machine learning futebol"
    },
    {
      title: "Estatísticas Avançadas",
      icon: BarChart3,
      duration: "8 min",
      topics: [
        "xG (Expected Goals)",
        "xA (Expected Assists)",
        "PPDA (Passes per Defensive Action)",
        "Progressive Carries",
        "Pressing Intensity"
      ],
      keywords: "xG futebol, estatísticas avançadas futebol, métricas futebol, análise tática"
    },
    {
      title: "Análise de Desempenho",
      icon: TrendingUp,
      duration: "7 min",
      topics: [
        "Form Rating (Forma recente)",
        "Home/Away Performance",
        "Head-to-Head histórico",
        "Fatores psicológicos",
        "Análise de motivação"
      ],
      keywords: "análise desempenho futebol, forma time futebol, histórico confrontos, psicologia esportiva"
    },
    {
      title: "Fatores Contextuais",
      icon: Search,
      duration: "6 min",
      topics: [
        "Calendário e rotação",
        "Lesões e suspensões",
        "Condições climáticas",
        "Importância da partida",
        "Pressão da torcida"
      ],
      keywords: "fatores jogo futebol, análise contextual futebol, variáveis futebol, condições jogo"
    },
    {
      title: "Modelos Preditivos",
      icon: Calculator,
      duration: "10 min",
      topics: [
        "Poisson Distribution",
        "Regressão logística",
        "Random Forest",
        "Neural Networks",
        "Ensemble Methods"
      ],
      keywords: "modelos preditivos futebol, machine learning futebol, algoritmos futebol, previsão jogos"
    },
    {
      title: "Interpretação de Dados",
      icon: Lightbulb,
      duration: "9 min",
      topics: [
        "Leitura de probabilidades",
        "Margem de erro",
        "Intervalos de confiança",
        "Viés e variância",
        "Overfitting e underfitting"
      ],
      keywords: "interpretar dados futebol, probabilidades futebol, análise estatística futebol, margem erro"
    }
  ];

  const glossary = [
    {
      term: "xG (Expected Goals)",
      definition: "Métrica que calcula a probabilidade de um chute virar gol baseado em fatores como posição, ângulo, tipo de assistência, parte do corpo usada.",
      example: "Um pênalti tem xG de 0.76 (76% de chance de gol)"
    },
    {
      term: "PPDA",
      definition: "Passes Per Defensive Action - mede a intensidade de pressão de um time. Quanto menor o número, maior a pressão.",
      example: "Liverpool tem PPDA de 7.8 (alta pressão)"
    },
    {
      term: "Progressive Carries",
      definition: "Conduções de bola que avançam significativamente em direção ao gol adversário (mínimo 10 metros ou para o terço final).",
      example: "Neymar lidera com 8.2 progressive carries por jogo"
    },
    {
      term: "Heat Map",
      definition: "Representação visual das áreas do campo onde um jogador ou time mais atua durante a partida.",
      example: "Messi tem heat map concentrado na meia-direita"
    },
    {
      term: "Overperformance",
      definition: "Quando um time/jogador está superando suas métricas esperadas (scoring mais gols que o xG sugere).",
      example: "Flamengo com +8 gols acima do xG (overperforming)"
    },
    {
      term: "Regressão à Média",
      definition: "Tendência estatística de resultados extremos voltarem à média ao longo do tempo.",
      example: "Time com 10 vitórias seguidas tende a equilibrar"
    },
    {
      term: "Poisson Distribution",
      definition: "Modelo estatístico usado para prever número de gols em uma partida baseado em médias históricas.",
      example: "Modelo prevê 2.3 gols no jogo com 67% confiança"
    },
    {
      term: "Form Rating",
      definition: "Avaliação numérica do desempenho recente de um time, geralmente últimos 5-10 jogos.",
      example: "Palmeiras com form rating 8.5/10 (excelente)"
    }
  ];

  const researchTools = [
    {
      name: "Análise de Padrões",
      description: "Identificação de tendências e padrões em grandes volumes de dados históricos",
      features: ["15 anos de dados", "8000+ jogos analisados", "Pro variáveis por jogo"],
      useCase: "Descobrir que times jogam melhor após derrotas em casa"
    },
    {
      name: "Simulação Monte Carlo",
      description: "Execução de milhares de simulações para calcular probabilidades precisas",
      features: ["10.000 simulações/jogo", "Variação de cenários", "Intervalos de confiança"],
      useCase: "Calcular chance real de classificação na tabela"
    },
    {
      name: "Análise Sentiment",
      description: "Processamento de linguagem natural para analisar notícias e redes sociais",
      features: ["Twitter/news mining", "Detecção de lesões", "Moral do time"],
      useCase: "Detectar problemas internos não divulgados"
    },
    {
      name: "Computer Vision",
      description: "Análise de vídeo para extrair dados táticos e de movimento",
      features: ["Tracking de jogadores", "Análise de espaços", "Padrões de jogo"],
      useCase: "Identificar mudanças táticas em tempo real"
    }
  ];

  const ethicalGuidelines = [
    {
      principle: "Educação Primeiro",
      description: "Nossa missão é educar sobre análise de dados, não incentivar apostas",
      icon: GraduationCap
    },
    {
      principle: "Transparência Total",
      description: "Sempre explicamos como chegamos às conclusões e margem de erro",
      icon: Info
    },
    {
      principle: "Jogo Responsável",
      description: "Promovemos o uso de dados para conhecimento, não vício",
      icon: Shield
    },
    {
      principle: "Sem Garantias",
      description: "Futebol é imprevisível - fornecemos análises, não certezas",
      icon: AlertCircle
    }
  ];

  const practicalExercises = [
    {
      title: "Exercício 1: Analisando xG",
      question: "Time A teve 2.3 xG e fez 1 gol. Time B teve 0.8 xG e fez 2 gols. O que isso indica?",
      answer: "Time A está underperforming (desperdiçando chances), Time B overperforming (eficiência acima da média). Tendência é reversão futura.",
      difficulty: "Básico"
    },
    {
      title: "Exercício 2: Form vs Histórico",
      question: "Flamengo venceu 8 dos últimos 10 jogos, mas perdeu 6 dos últimos 8 confrontos contra o Palmeiras. Como analisar?",
      answer: "Form atual sugere Flamengo favorito, mas H2H indica dificuldade específica. Ponderar 60% form, 40% H2H na análise.",
      difficulty: "Intermediário"
    },
    {
      title: "Exercício 3: Fatores Contextuais",
      question: "Grêmio joga quinta (Libertadores) e domingo (Brasileirão). Como isso afeta a análise?",
      answer: "Provável rotação no Brasileirão, especialmente se Libertadores for mata-mata. Reduzir expectativa de performance em 20-30%.",
      difficulty: "Avançado"
    }
  ];

  const faqs = [
    {
      question: "PickStar é um site de apostas?",
      answer: "NÃO! PickStar é uma ferramenta de pesquisa e educação que usa IA para analisar dados do futebol. Fornecemos insights e análises para fins educacionais, ajudando você a entender melhor o esporte através de dados."
    },
    {
      question: "Como a IA pode prever resultados de futebol?",
      answer: "A IA não 'prevê' com certeza - ela calcula probabilidades baseadas em padrões históricos, análise de milhares de variáveis e modelos estatísticos. É como a previsão do tempo: usa dados para estimar o mais provável, mas sempre há margem de erro."
    },
    {
      question: "Qual a diferença entre análise e palpite?",
      answer: "Análise é baseada em dados, estatísticas e métodos científicos. Palpite é opinião pessoal, muitas vezes influenciada por emoção. Nossa IA faz análise pura, sem viés emocional ou preferências."
    },
    {
      question: "Posso usar esses dados para apostar?",
      answer: "Nosso foco é educacional. Se você escolher usar informações para qualquer fim, faça-o com responsabilidade, dentro de seus limites e consciente de que esporte é imprevisível. Nunca aposte mais do que pode perder."
    },
    {
      question: "Como aprender a interpretar os dados?",
      answer: "Comece com conceitos básicos como xG e form rating. Pratique com jogos passados para entender como métricas se correlacionam com resultados. Use nossos tutoriais interativos e sempre questione os dados."
    }
  ];

  // Structured Data for SEO
  const courseStructuredData = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "PickStar: Sua Sala de Aula Virtual para Apostas Esportivas Educacionais",
    "description": "Aprenda sobre apostas esportivas e fantasy sports de forma responsável com o chat do PickStar. Análise com IA, estatísticas avançadas e aprendizado contínuo.",
    "provider": {
      "@type": "Organization",
      "name": "PickStarAI",
      "sameAs": "https://pickstarai.com"
    },
    "courseMode": "online",
    "isAccessibleForFree": true,
    "hasCourseInstance": {
      "@type": "CourseInstance",
      "courseMode": "online",
      "startDate": "2024-01-01",
      "endDate": "2024-12-31"
    }
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <SEOHead 
        title="PickStar: Aprenda Apostas Esportivas e Fantasy Sports com IA | Tutorial 2024"
        description="O PickStar é sua sala de aula virtual para aprender sobre apostas esportivas e fantasy sports de forma educacional e responsável. Use nosso chat com IA para aprender estatísticas avançadas, análise de dados e estratégias."
        keywords="pickstar tutorial, apostas esportivas educacional, fantasy sports brasil, inteligência artificial apostas, análise futebol ia, estatísticas avançadas futebol, aprender apostas responsável, chat ia esportes, educação apostas esportivas, estratégias fantasy, análise dados futebol, tutorial apostas brasil"
        canonical="https://pickstarai.com/pt-br/tutorial-ia-futebol"
        ogImage="https://pickstarai.com/og-tutorial.jpg"
        structuredData={[courseStructuredData, faqStructuredData]}
      />
      
      <div className="min-h-screen bg-white">
        {/* Hero Section - Educational Focus */}
        <motion.header 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative bg-gradient-to-br from-gray-50 to-gray-100 py-12 md:py-20"
        >
          <div className="container mx-auto px-4">
            <div className="text-center max-w-4xl mx-auto">
              <motion.div 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="flex justify-center mb-6"
              >
                <div className="w-16 h-16 bg-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <GraduationCap className="w-8 h-8 text-white" />
                </div>
              </motion.div>
              
              <motion.h1 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight"
              >
                PickStar é Sua Sala de Aula
                <span className="block text-indigo-600 mt-2">Aprenda IA e Análise de Dados no Futebol</span>
              </motion.h1>
              
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.6 }}
                className="text-lg md:text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed"
              >
                O PickStar é onde você aprende sobre apostas esportivas e fantasy sports de forma educacional e responsável. 
                Siga o guia abaixo e pergunte ao chat do PickStar para aprender tudo sobre análise esportiva com IA.
              </motion.p>
              
              <motion.button
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5, duration: 0.6 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleStartChat}
                className="inline-flex items-center gap-3 px-8 py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold text-lg transition-all duration-200 shadow-lg hover:shadow-xl"
                aria-label="Acessar o chat do PickStar para aprender"
              >
                <BookOpen className="w-5 h-5" aria-hidden="true" />
                Acessar o Chat do PickStar
                <ChevronRight className="w-5 h-5" aria-hidden="true" />
              </motion.button>

              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6, duration: 0.6 }}
                className="mt-6 flex flex-wrap items-center justify-center gap-4 text-sm text-gray-500"
              >
                <span className="flex items-center gap-1">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  100% Educacional
                </span>
                <span className="flex items-center gap-1">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Análise Responsável
                </span>
                <span className="flex items-center gap-1">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Aprenda com IA
                </span>
              </motion.div>
            </div>
          </div>
        </motion.header>

        {/* Quick Stats - Educational Focus */}
        <motion.section 
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="py-12 bg-white border-b border-gray-100" 
          aria-labelledby="edu-stats-heading"
        >
          <div className="container mx-auto px-4">
            <h2 id="edu-stats-heading" className="sr-only">Estatísticas do Tutorial</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="text-center p-4 rounded-xl bg-gradient-to-br from-indigo-50 to-indigo-100 border border-indigo-200"
              >
                <div className="text-3xl md:text-4xl font-bold text-indigo-600 mb-2">50h</div>
                <p className="text-sm text-gray-600">Conteúdo educacional</p>
              </motion.div>
              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="text-center p-4 rounded-xl bg-gradient-to-br from-green-50 to-green-100 border border-green-200"
              >
                <div className="text-3xl md:text-4xl font-bold text-green-600 mb-2">Pro</div>
                <p className="text-sm text-gray-600">Variáveis analisadas</p>
              </motion.div>
              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="text-center p-4 rounded-xl bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200"
              >
                <div className="text-3xl md:text-4xl font-bold text-purple-600 mb-2">IA</div>
                <p className="text-sm text-gray-600">Análise Inteligente</p>
              </motion.div>
              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="text-center p-4 rounded-xl bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200"
              >
                <div className="text-3xl md:text-4xl font-bold text-purple-600 mb-2">Grátis</div>
                <p className="text-sm text-gray-600">Para sempre</p>
              </motion.div>
            </div>
          </div>
        </motion.section>

        {/* Main Content */}
        <main className="container mx-auto px-4 py-12">
          
          {/* What is PickStar - Educational Positioning */}
          <motion.section 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="mb-16" 
            aria-labelledby="about-heading"
          >
            <h2 id="about-heading" className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
              <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                <Brain className="w-5 h-5 text-white" aria-hidden="true" />
              </div>
              O Que é PickStar: Ferramenta de Pesquisa com IA
            </h2>
            
            <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-sm">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.6 }}
                className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-6 mb-6"
              >
                <p className="text-lg text-gray-900 font-semibold mb-3 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-indigo-600" />
                  PickStar é Sua Sala de Aula Virtual!
                </p>
                <p className="text-gray-700 leading-relaxed">
                  O PickStar é onde você aprende sobre apostas esportivas e fantasy sports de forma educacional e responsável. 
                  Use nosso chat com IA para fazer perguntas, receber análises detalhadas e aprender tudo sobre estatísticas esportivas avançadas.
                </p>
              </motion.div>
              
              <div className="grid md:grid-cols-3 gap-6">
                <motion.div 
                  whileHover={{ y: -5 }}
                  className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl p-6 border border-indigo-200"
                >
                  <h3 className="font-bold text-indigo-700 mb-3 flex items-center gap-2">
                    <Database className="w-5 h-5" />
                    Pesquisa Avançada
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Análise de 15 anos de dados, Pro variáveis por jogo, modelos de machine learning
                  </p>
                </motion.div>
                <motion.div 
                  whileHover={{ y: -5 }}
                  className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border border-green-200"
                >
                  <h3 className="font-bold text-green-700 mb-3 flex items-center gap-2">
                    <GraduationCap className="w-5 h-5" />
                    100% Educacional
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Tutoriais, exercícios práticos, aprendizado contínuo, análises personalizadas
                  </p>
                </motion.div>
                <motion.div 
                  whileHover={{ y: -5 }}
                  className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border border-purple-200"
                >
                  <h3 className="font-bold text-purple-700 mb-3 flex items-center gap-2">
                    <Cpu className="w-5 h-5" />
                    IA Transparente
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Explicamos como funciona cada algoritmo e modelo usado em nossas análises
                  </p>
                </motion.div>
              </div>
            </div>
          </motion.section>

          {/* Interactive Lessons */}
          <motion.section 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="mb-16" 
            aria-labelledby="lessons-heading"
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
              <h2 id="lessons-heading" className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 sm:mb-0 flex items-center gap-3">
                <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-white" aria-hidden="true" />
                </div>
                Guia de Aprendizado: Do Básico ao Avançado
              </h2>
              
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-400" />
                <select 
                  value={filterLevel}
                  onChange={(e) => setFilterLevel(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="all">Todos os níveis</option>
                  <option value="beginner">Iniciante</option>
                  <option value="intermediate">Intermediário</option>
                  <option value="advanced">Avançado</option>
                </select>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <AnimatePresence>
                {lessons.map((lesson, index) => {
                  const Icon = lesson.icon;
                  return (
                    <motion.article 
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ y: -5 }}
                      className={`bg-white rounded-xl p-6 cursor-pointer transition-all border-2 shadow-sm hover:shadow-md ${
                        activeLesson === index 
                          ? 'border-purple-500 shadow-purple-100' 
                          : 'border-gray-200 hover:border-purple-300'
                      }`}
                      onClick={() => setActiveLesson(index)}
                    >
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center shadow-md">
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="text-gray-900 font-bold text-lg">Módulo {index + 1}: {lesson.title}</h3>
                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">{lesson.duration}</span>
                          </div>
                          <ul className="space-y-2 mb-4">
                            {lesson.topics.map((topic, idx) => (
                              <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                                <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                                <span>{topic}</span>
                              </li>
                            ))}
                          </ul>
                          <p className="text-xs text-purple-600 bg-purple-50 px-3 py-2 rounded-lg border border-purple-200">
                            <strong>Palavras-chave:</strong> {lesson.keywords}
                          </p>
                        </div>
                      </div>
                    </motion.article>
                  );
                })}
              </AnimatePresence>
            </div>
          </motion.section>

          {/* Glossary Section */}
          <motion.section 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="mb-16" 
            aria-labelledby="glossary-heading"
          >
            <h2 id="glossary-heading" className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
              <div className="w-8 h-8 bg-cyan-600 rounded-lg flex items-center justify-center">
                <Database className="w-5 h-5 text-white" aria-hidden="true" />
              </div>
              Glossário: Termos Essenciais da Análise de Dados
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {glossary.map((item, index) => (
                <motion.article 
                  key={index} 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-md transition-all"
                >
                  <button
                    onClick={() => toggleSection(`glossary-${index}`)}
                    className="w-full p-6 text-left hover:bg-gray-50 transition-colors"
                    aria-expanded={expandedSections[`glossary-${index}`]}
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-bold text-cyan-700">{item.term}</h3>
                      <ChevronDown 
                        className={`w-5 h-5 text-gray-400 transition-transform ${
                          expandedSections[`glossary-${index}`] ? 'rotate-180' : ''
                        }`} 
                      />
                    </div>
                  </button>
                  
                  <AnimatePresence>
                    {expandedSections[`glossary-${index}`] && (
                      <motion.div 
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="px-6 pb-6"
                      >
                        <p className="text-sm text-gray-600 mb-4 leading-relaxed">{item.definition}</p>
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
                          <p className="text-sm text-green-700">
                            <strong>Exemplo:</strong> {item.example}
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.article>
              ))}
            </div>
          </motion.section>

          {/* Research Tools */}
          <motion.section 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="mb-16" 
            aria-labelledby="tools-heading"
          >
            <h2 id="tools-heading" className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
              <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
                <Cpu className="w-5 h-5 text-white" aria-hidden="true" />
              </div>
              Ferramentas de Pesquisa com IA
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {researchTools.map((tool, index) => (
                <motion.article 
                  key={index} 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.2 }}
                  whileHover={{ y: -5 }}
                  className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-all"
                >
                  <h3 className="text-xl font-bold text-green-700 mb-3">{tool.name}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{tool.description}</p>
                  
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Recursos:</h4>
                    <ul className="space-y-2">
                      {tool.features.map((feature, idx) => (
                        <li key={idx} className="text-sm text-gray-600 flex items-center gap-3">
                          <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg p-4 border border-indigo-200">
                    <p className="text-sm text-indigo-800">
                      <strong className="text-indigo-900">Caso de uso:</strong> {tool.useCase}
                    </p>
                  </div>
                </motion.article>
              ))}
            </div>
          </motion.section>

          {/* Practical Exercises */}
          <motion.section 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="mb-16" 
            aria-labelledby="exercises-heading"
          >
            <h2 id="exercises-heading" className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
              <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
                <Target className="w-5 h-5 text-white" aria-hidden="true" />
              </div>
              Exercícios Práticos: Teste Seu Conhecimento
            </h2>
            
            <div className="space-y-6">
              {practicalExercises.map((exercise, index) => (
                <motion.article 
                  key={index} 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.2 }}
                  className="bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-md transition-all"
                >
                  <button
                    onClick={() => toggleSection(`exercise-${index}`)}
                    className="w-full p-6 text-left hover:bg-gray-50 transition-colors"
                    aria-expanded={expandedSections[`exercise-${index}`]}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <h3 className="text-lg font-bold text-gray-900">{exercise.title}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          exercise.difficulty === 'Básico' ? 'bg-green-100 text-green-700 border border-green-300' :
                          exercise.difficulty === 'Intermediário' ? 'bg-indigo-100 text-indigo-700 border border-indigo-300' :
                          'bg-purple-100 text-purple-700 border border-purple-300'
                        }`}>
                          {exercise.difficulty}
                        </span>
                      </div>
                      <ChevronDown 
                        className={`w-5 h-5 text-gray-400 transition-transform ${
                          expandedSections[`exercise-${index}`] ? 'rotate-180' : ''
                        }`} 
                      />
                    </div>
                  </button>
                  
                  <AnimatePresence>
                    {expandedSections[`exercise-${index}`] && (
                      <motion.div 
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="px-6 pb-6"
                      >
                        <div className="mb-6">
                          <h4 className="text-sm font-semibold text-indigo-700 mb-3">Pergunta:</h4>
                          <p className="text-sm text-gray-600 leading-relaxed">{exercise.question}</p>
                        </div>
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
                          <h4 className="text-sm font-semibold text-green-700 mb-3">Resposta:</h4>
                          <p className="text-sm text-green-800 leading-relaxed">{exercise.answer}</p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.article>
              ))}
            </div>
          </motion.section>

          {/* Ethical Guidelines */}
          <motion.section 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="mb-16" 
            aria-labelledby="ethics-heading"
          >
            <h2 id="ethics-heading" className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" aria-hidden="true" />
              </div>
              Princípios Éticos e Uso Responsável
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {ethicalGuidelines.map((guideline, index) => {
                const Icon = guideline.icon;
                return (
                  <motion.article 
                    key={index} 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ y: -5 }}
                    className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-all"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center border border-blue-200">
                        <Icon className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-gray-900 font-bold mb-2">{guideline.principle}</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">{guideline.description}</p>
                      </div>
                    </div>
                  </motion.article>
                );
              })}
            </div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.6 }}
              className="mt-8 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6"
            >
              <p className="text-sm text-gray-700 leading-relaxed">
                <strong className="text-blue-700">Lembrete:</strong> O futebol é imprevisível por natureza. 
                Nenhuma análise, por mais avançada que seja, pode garantir resultados. Use o conhecimento 
                adquirido para apreciar melhor o esporte, não para atividades que possam causar prejuízo.
              </p>
            </motion.div>
          </motion.section>

          {/* How AI Works in Football */}
          <motion.section 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="mb-16" 
            aria-labelledby="ai-works-heading"
          >
            <h2 id="ai-works-heading" className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
              <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
                <Brain className="w-5 h-5 text-white" aria-hidden="true" />
              </div>
              Como a IA Analisa o Futebol: Processo Completo
            </h2>
            
            <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-sm">
              <div className="space-y-8">
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6, duration: 0.6 }}
                  className="flex items-start gap-6"
                >
                  <div className="flex-shrink-0 w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold shadow-md">1</div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3">Coleta de Dados</h3>
                    <p className="text-gray-600 mb-3 leading-relaxed">
                      Coletamos dados de múltiplas fontes: estatísticas oficiais, tracking de jogadores, 
                      condições climáticas, notícias, redes sociais.
                    </p>
                    <p className="text-sm text-purple-600 bg-purple-50 px-3 py-2 rounded-lg border border-purple-200">
                      <strong>Palavras-chave:</strong> web scraping, APIs esportivas, data mining, ETL process
                    </p>
                  </div>
                </motion.div>

                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.7, duration: 0.6 }}
                  className="flex items-start gap-6"
                >
                  <div className="flex-shrink-0 w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold shadow-md">2</div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3">Processamento e Limpeza</h3>
                    <p className="text-gray-600 mb-3 leading-relaxed">
                      Padronizamos formatos, removemos outliers, tratamos dados faltantes, 
                      normalizamos escalas para comparação justa.
                    </p>
                    <p className="text-sm text-purple-600 bg-purple-50 px-3 py-2 rounded-lg border border-purple-200">
                      <strong>Palavras-chave:</strong> data cleaning, normalização, outlier detection, missing data
                    </p>
                  </div>
                </motion.div>

                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.8, duration: 0.6 }}
                  className="flex items-start gap-6"
                >
                  <div className="flex-shrink-0 w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold shadow-md">3</div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3">Feature Engineering</h3>
                    <p className="text-gray-600 mb-3 leading-relaxed">
                      Criamos variáveis derivadas como xG, form rating, momentum, fatigue index 
                      que capturam aspectos complexos do jogo.
                    </p>
                    <p className="text-sm text-purple-600 bg-purple-50 px-3 py-2 rounded-lg border border-purple-200">
                      <strong>Palavras-chave:</strong> feature extraction, dimensionality reduction, PCA, correlation analysis
                    </p>
                  </div>
                </motion.div>

                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.9, duration: 0.6 }}
                  className="flex items-start gap-6"
                >
                  <div className="flex-shrink-0 w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold shadow-md">4</div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3">Modelagem com Machine Learning</h3>
                    <p className="text-gray-600 mb-3 leading-relaxed">
                      Aplicamos algoritmos como Random Forest, XGBoost, Neural Networks para 
                      identificar padrões e fazer previsões probabilísticas.
                    </p>
                    <p className="text-sm text-purple-600 bg-purple-50 px-3 py-2 rounded-lg border border-purple-200">
                      <strong>Palavras-chave:</strong> supervised learning, ensemble methods, deep learning, cross-validation
                    </p>
                  </div>
                </motion.div>

                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.0, duration: 0.6 }}
                  className="flex items-start gap-6"
                >
                  <div className="flex-shrink-0 w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold shadow-md">5</div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3">Validação e Interpretação</h3>
                    <p className="text-gray-600 mb-3 leading-relaxed">
                      Testamos modelos com dados históricos, calculamos margem de erro, 
                      interpretamos resultados em contexto futebolístico.
                    </p>
                    <p className="text-sm text-purple-600 bg-purple-50 px-3 py-2 rounded-lg border border-purple-200">
                      <strong>Palavras-chave:</strong> backtesting, confidence intervals, SHAP values, model interpretability
                    </p>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.section>

          {/* Learning Path */}
          <motion.section 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="mb-16" 
            aria-labelledby="path-heading"
          >
            <h2 id="path-heading" className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
              <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" aria-hidden="true" />
              </div>
              Trilha de Aprendizado: Seu Caminho na Ciência de Dados Esportiva
            </h2>
            
            <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-sm">
              <div className="space-y-6">
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.7, duration: 0.6 }}
                  className="border-l-4 border-green-500 pl-6 bg-green-50 rounded-r-lg p-4"
                >
                  <h3 className="text-lg font-bold text-green-700 mb-3">🎯 Nível Iniciante (1-2 meses)</h3>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li>• Conceitos básicos: média, mediana, desvio padrão</li>
                    <li>• Introdução ao xG e estatísticas básicas</li>
                    <li>• Leitura de probabilidades e odds</li>
                    <li>• Análise de forma e histórico H2H</li>
                    <li>• Excel/Google Sheets para análise básica</li>
                  </ul>
                </motion.div>

                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.8, duration: 0.6 }}
                  className="border-l-4 border-indigo-500 pl-6 bg-indigo-50 rounded-r-lg p-4"
                >
                  <h3 className="text-lg font-bold text-indigo-700 mb-3">📊 Nível Intermediário (3-6 meses)</h3>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li>• Python básico para análise de dados</li>
                    <li>• Bibliotecas: Pandas, NumPy, Matplotlib</li>
                    <li>• Web scraping com BeautifulSoup</li>
                    <li>• Modelos simples: regressão linear, logística</li>
                    <li>• Visualização de dados com Plotly</li>
                  </ul>
                </motion.div>

                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.9, duration: 0.6 }}
                  className="border-l-4 border-blue-500 pl-6 bg-blue-50 rounded-r-lg p-4"
                >
                  <h3 className="text-lg font-bold text-blue-700 mb-3">🚀 Nível Avançado (6-12 meses)</h3>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li>• Machine Learning com Scikit-learn</li>
                    <li>• Deep Learning com TensorFlow/PyTorch</li>
                    <li>• Computer Vision para análise tática</li>
                    <li>• NLP para análise de sentimento</li>
                    <li>• Deployment de modelos em produção</li>
                  </ul>
                </motion.div>

                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.0, duration: 0.6 }}
                  className="border-l-4 border-purple-500 pl-6 bg-purple-50 rounded-r-lg p-4"
                >
                  <h3 className="text-lg font-bold text-purple-700 mb-3">🏆 Nível Expert (12+ meses)</h3>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li>• Arquiteturas de IA proprietárias</li>
                    <li>• Reinforcement Learning para estratégias</li>
                    <li>• Real-time analytics e streaming data</li>
                    <li>• Publicação de papers acadêmicos</li>
                    <li>• Consultoria para clubes profissionais</li>
                  </ul>
                </motion.div>
              </div>
            </div>
          </motion.section>

          {/* Community Resources */}
          <motion.section 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="mb-16" 
            aria-labelledby="community-heading"
          >
            <h2 id="community-heading" className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-white" aria-hidden="true" />
              </div>
              Recursos do PickStar: Ferramentas para Aprender
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              <motion.article 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.6 }}
                whileHover={{ y: -5 }}
                className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-all"
              >
                <h3 className="text-lg font-bold text-blue-700 mb-4">📚 Biblioteca de Datasets</h3>
                <ul className="space-y-3 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full" />
                    15 anos de dados Brasileirão
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full" />
                    Estatísticas detalhadas por jogador
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full" />
                    Dados climáticos históricos
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full" />
                    Tracking data de movimentação
                  </li>
                </ul>
              </motion.article>

              <motion.article 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9, duration: 0.6 }}
                whileHover={{ y: -5 }}
                className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-all"
              >
                <h3 className="text-lg font-bold text-green-700 mb-4">💬 Chat do PickStar</h3>
                <ul className="space-y-3 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full" />
                    Perguntas e respostas em tempo real
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full" />
                    Análises personalizadas
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full" />
                    Explicações detalhadas
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full" />
                    Aprendizado contínuo
                  </li>
                </ul>
              </motion.article>

              <motion.article 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.0, duration: 0.6 }}
                whileHover={{ y: -5 }}
                className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-all"
              >
                <h3 className="text-lg font-bold text-purple-700 mb-4">🎓 Método de Aprendizado</h3>
                <ul className="space-y-3 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-purple-500 rounded-full" />
                    Siga o guia passo a passo
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-purple-500 rounded-full" />
                    Faça perguntas ao chat
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-purple-500 rounded-full" />
                    Pratique com exemplos reais
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-purple-500 rounded-full" />
                    Aprenda no seu ritmo
                  </li>
                </ul>
              </motion.article>
            </div>
          </motion.section>

          {/* FAQ Section */}
          <motion.section 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8 }}
            className="mb-16" 
            aria-labelledby="faq-heading"
          >
            <h2 id="faq-heading" className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
              <div className="w-8 h-8 bg-cyan-600 rounded-lg flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-white" aria-hidden="true" />
              </div>
              Perguntas Frequentes sobre IA e Futebol
            </h2>
            
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <motion.article 
                  key={index} 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-md transition-all"
                >
                  <button
                    onClick={() => toggleSection(`faq-${index}`)}
                    className="w-full p-6 text-left hover:bg-gray-50 transition-colors"
                    aria-expanded={expandedSections[`faq-${index}`]}
                    aria-controls={`faq-answer-${index}`}
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 pr-4">
                        {faq.question}
                      </h3>
                      <ChevronDown 
                        className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${
                          expandedSections[`faq-${index}`] ? 'rotate-180' : ''
                        }`} 
                      />
                    </div>
                  </button>
                  
                  <AnimatePresence>
                    {expandedSections[`faq-${index}`] && (
                      <motion.div 
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        id={`faq-answer-${index}`} 
                        className="px-6 pb-6 text-gray-600 leading-relaxed"
                      >
                        {faq.answer}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.article>
              ))}
            </div>
          </motion.section>

          {/* What You'll Learn */}
          <motion.section 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mb-16" 
            aria-labelledby="success-heading"
          >
            <h2 id="success-heading" className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
              <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                <Award className="w-5 h-5 text-white" aria-hidden="true" />
              </div>
              O Que Você Aprenderá com o PickStar
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <motion.article 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.0, duration: 0.6 }}
                whileHover={{ y: -5 }}
                className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-200 shadow-sm hover:shadow-md transition-all"
              >
                <h3 className="text-lg font-bold text-gray-900 mb-3">Análise Estatística Avançada</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  Entenda métricas como xG, xA, PPDA e outras estatísticas avançadas. 
                  Pergunte ao chat do PickStar sobre qualquer métrica e receba explicações detalhadas com exemplos práticos.
                </p>
                <p className="text-sm text-indigo-700 font-semibold">
                  Aprenda no seu ritmo com IA
                </p>
              </motion.article>

              <motion.article 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.1, duration: 0.6 }}
                whileHover={{ y: -5 }}
                className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200 shadow-sm hover:shadow-md transition-all"
              >
                <h3 className="text-lg font-bold text-gray-900 mb-3">Apostas Responsáveis</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  Aprenda a analisar jogos de forma educacional e responsável. 
                  O PickStar ensina você a entender probabilidades e gerenciar riscos de forma inteligente.
                </p>
                <p className="text-sm text-green-700 font-semibold">
                  Educação antes de tudo
                </p>
              </motion.article>

              <motion.article 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2, duration: 0.6 }}
                whileHover={{ y: -5 }}
                className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200 shadow-sm hover:shadow-md transition-all"
              >
                <h3 className="text-lg font-bold text-gray-900 mb-3">Fantasy Sports</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  Domine as estratégias de fantasy sports com análises baseadas em dados. 
                  Use o chat para receber dicas personalizadas para montar seu time ideal.
                </p>
                <p className="text-sm text-blue-700 font-semibold">
                  Estratégias baseadas em dados
                </p>
              </motion.article>

              <motion.article 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.3, duration: 0.6 }}
                whileHover={{ y: -5 }}
                className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200 shadow-sm hover:shadow-md transition-all"
              >
                <h3 className="text-lg font-bold text-gray-900 mb-3">Tendências e Padrões</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  Descubra como identificar tendências e padrões nos jogos. 
                  O PickStar analisa milhares de dados para mostrar insights que você não veria sozinho.
                </p>
                <p className="text-sm text-purple-700 font-semibold">
                  IA trabalhando para você
                </p>
              </motion.article>
            </div>
          </motion.section>

        </main>

        {/* CTA Section - Educational Focus */}
        <motion.section 
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.8 }}
          className="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 border-t border-gray-200"
        >
          <div className="container mx-auto px-4 py-16 text-center">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.6 }}
              className="text-3xl md:text-4xl font-bold text-gray-900 mb-6"
            >
              Comece Sua Jornada de Aprendizado no PickStar!
            </motion.h2>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.6 }}
              className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed"
            >
              O PickStar é sua sala de aula virtual para aprender sobre apostas esportivas e fantasy sports 
              de forma responsável e educacional. Pergunte ao nosso chat e aprenda com IA!
            </motion.p>
            
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.9, duration: 0.6 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleStartChat}
              className="inline-flex items-center gap-3 px-8 py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold text-lg transition-all duration-200 shadow-lg hover:shadow-xl mb-8"
              aria-label="Acessar o chat do PickStar para aprender"
            >
              <GraduationCap className="w-5 h-5" aria-hidden="true" />
              Acessar o Chat do PickStar Agora
              <ChevronRight className="w-5 h-5" aria-hidden="true" />
            </motion.button>
            
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1, duration: 0.6 }}
              className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto"
            >
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>Análise com IA</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>Aprendizado Responsável</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>Respostas em Tempo Real</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>100% Educacional</span>
              </div>
            </motion.div>
          </div>
        </motion.section>

        <SEOFooter language="pt-BR" />
      </div>

      {/* Structured Data Scripts */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(courseStructuredData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
      />
    </>
  );
};

export default TutorialAIFutebolPage;